import { Component } from '@theme/component';
import { clamp } from '@theme/utilities';
import { Scroller } from '@theme/scrolling';

/**
 * Mobile scroll-snap carousel for group blocks with dot pagination.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLButtonElement[]} [dots]
 *
 * @extends {Component<Refs>}
 */
class GroupCarousel extends Component {
  requiredRefs = ['scroller'];

  /** @type {Scroller | undefined} */
  #scroll;

  connectedCallback() {
    super.connectedCallback();

    this.#scroll = new Scroller(this.refs.scroller, { onScroll: this.#handleScroll });
    this.#handleScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scroll?.destroy();
  }

  get #columns() {
    const columns = Number(this.dataset.columns) || 1;
    return Math.max(1, columns);
  }

  get #slides() {
    return Array.from(this.refs.scroller.children).filter((child) => child instanceof HTMLElement);
  }

  /**
   * @param {number} index - Dot index (page index)
   */
  select(index) {
    const { dots } = this.refs;
    const slides = this.#slides;
    if (!slides.length) return;

    const dotIndex = clamp(index, 0, (dots?.length ?? 1) - 1);
    const slideIndex = clamp(dotIndex * this.#columns, 0, slides.length - 1);
    const slide = slides[slideIndex];

    if (slide) {
      this.#scroll?.to(slide);
    }

    this.#updateDots(dotIndex);
  }

  #handleScroll = () => {
    const slides = this.#slides;
    const { dots } = this.refs;
    if (!slides.length || !dots?.length) return;

    const scroller = this.refs.scroller;
    const scrollLeft = scroller.scrollLeft;
    let activeIndex = 0;
    let minDistance = Infinity;

    for (const [index, slide] of slides.entries()) {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    }

    const dotIndex = Math.floor(activeIndex / this.#columns);
    this.#updateDots(dotIndex);
  };

  /**
   * @param {number} index
   */
  #updateDots(index) {
    const { dots } = this.refs;
    if (!dots?.length) return;

    const selectedIndex = clamp(index, 0, dots.length - 1);

    for (const [i, dot] of dots.entries()) {
      dot.setAttribute('aria-current', String(i === selectedIndex));
    }
  }
}

if (!customElements.get('group-carousel-component')) {
  customElements.define('group-carousel-component', GroupCarousel);
}
